import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from "react";

import { analytics } from "@analytics";

export enum UserJourney {
  FEED_DISCOVER = "FeedDiscover",
  PROFILE = "Profile",
  CLAIM = "Claim",
  ONBOARDING = "Onboarding",
  CAROUSEL = "Carousel",
  GAMES = "Games",
}

export enum Action {
  LANDED = "Landed",
  WATCHED = "Watched",
  VIEWED = "Viewed",
  OPENED = "Opened",
  PRESSED = "Pressed",
  STARTED = "Started",
  PENDING = "Pending",
  SUCCEEDED = "Succeeded",
  FAILED = "Failed",
  IMPRESSION = "Impression",
  REQUEST = "Request",
  COMPLETED = "Completed",
  EARNED = "Earned",
  SKIPPED = "Skipped",
  CONNECT = "Connect",
  CONNECTED = "Connected",
}

export enum ControlType {
  BUTTON = "Button",
  LINK = "Link",
  IMAGE = "Image",
  EFFECT = "Effect",
  NOOP = "Noop",
}

export enum InteractionType {
  PRESS = "Press",
  WATCH = "Watch",
  ANSWER = "Answer",
  PLAY = "Play",
}

export enum TrackObject {
  FEED = "Feed",
  ONBOARDING = "Onboarding",
  DISCOVER_CONTENT = "DiscoverContent",
  PROFILE = "Profile",
  CLAIM = "Claim",
  STAKING = "Staking",
  LOGIN = "Login",
  REWARDS = "Rewards",
  REWARDS_HISTORY = "RewardsHistory",
  WALLETS = "Wallets",
  CAROUSEL = "Carousel",
  ADVERT = "Advert",
  NAVIGATION = "Navigation",
  BUTTON = "Button",
  WIDGET = "Widget",
}

export interface BaseAnalyticsExtras {
  [key: string]: unknown;
  screen: string;
  userJourney: UserJourney;
}

export interface FailedExtras {
  [key: string]: unknown;
}

interface BaseAnalytics {
  action: Action;
  extras: BaseAnalyticsExtras;
}

export type LinkTrackingEvent = {
  action: Action.PRESSED;
  extras: {
    controlType: ControlType.LINK;
    controlLabel: string;
    section: string;
    url: string;
  };
};

export type MenuItemTrackingEvent = {
  action: Action.PRESSED;
  extras: {
    id?: string;
    type?: string;
    label: string;
    section: string;
    parent?: string;
    url: string;
  };
};

type AnalyticsContextType = {
  trackV3: (props: {
    object: string;
    action: Action;
    extras?: Record<string, unknown>;
  }) => void;
};

// biome-ignore lint/style/noNonNullAssertion: Context is always provided
const AnalyticsContext = createContext<AnalyticsContextType>(undefined!);

export function AnalyticsProvider({ children }: PropsWithChildren) {
  const trackV3 = useCallback(
    ({
      object,
      action,
      extras = {},
    }: {
      object: string;
      action: Action;
      extras?: Record<string, unknown>;
    }) => {
      const eventName = `${object}_${action}`;
      analytics.track(eventName, extras);
    },
    [],
  );

  const contextValue = useMemo(() => ({ trackV3 }), [trackV3]);

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
}

/**
 * Hook to access analytics - only exposes trackV3
 */
export function useAnalytics(): AnalyticsContextType {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error("useAnalytics must be used within AnalyticsProvider");
  }
  return context;
}

/**
 * This function is a generic function used as a utility to create utility tracking hooks for different areas of the web app. This hook should not be used directly.
 *
 * @param object - The object to track.
 * @returns A function that can be used to track an event.
 *
 * @see ./discover.ts for a working example of creating the discover specific tracking hook.
 *
 * @example
 * `./discover.ts`
 * // Create a tracking hook for the discover feed
 * export const useDiscoverAnalytics = () => useAnalyticsTracker<DiscoverFeedEvent>(TrackObject.FEED);
 *
 * `DiscoverFeed.tsx`
 * // Track an event for the discover feed by consuming the above tracking hook
 * const { track } = useDiscoverAnalytics();
 *
 * track({
 *   action: Action.VIEWED,
 *   extras: { screen: "Home", userJourney: UserJourney.FEED_DISCOVER },
 * });
 */
export const useAnalyticsTracker = <
  T extends { action: Action; extras: Record<string, unknown> } | BaseAnalytics,
>(
  object: TrackObject,
) => {
  const { trackV3 } = useAnalytics();

  const track = useCallback(
    ({ action, extras }: T) => {
      trackV3({
        object,
        action,
        extras,
      });
    },
    [trackV3, object],
  );

  return useMemo(() => ({ track }), [track]);
};
